import React, { useState } from 'react';
import { Alert, Box, Button, Column, IconButton, Logo, TextField } from '@tempmee/react';
import { OutlineEyeIcon, OutlineEyeOffIcon } from '@tempmee/react-icons';
import GoogleLoginButton from '../commonComponents/GoogleLoginButton';
import { SessionScreen } from '../commonComponents/SessionScreen';
import { useSignIn } from '../../domain/Auth';

/**
 * Which sign-in methods this deployment offers, from
 * `REACT_APP_ENABLED_AUTH_PROVIDERS` (`credential`, `google`).
 *
 * An unset variable falls back to the credential form. An empty string does
 * not: `''.split(',')` is `['']`, which names neither provider, so the screen
 * offers no way in — kept as it was, and pinned by the auth flow tests.
 */
const readProviders = () => {
  const providers = process.env.REACT_APP_ENABLED_AUTH_PROVIDERS?.split(',') || [];
  return {
    credential: providers.length === 0 || providers.includes('credential'),
    google: providers.includes('google'),
  };
};

/** The backend's own words when it sent any; see "relay error bodies verbatim". */
const signInErrorMessage = (error) => {
  const body = error?.response?.data;
  const messages = (body?.errors ?? []).map((item) => item?.message || item).filter(Boolean);
  if (messages.length) return messages.join('\n');
  if (body?.error) return body.error;
  if (body?.message) return body.message;
  return 'Sign-in failed. Check your email and password and try again.';
};

const CredentialForm = () => {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const signIn = useSignIn();

  const submit = () => {
    if (!email || !password || signIn.isPending) return;
    signIn.mutate({ email, password });
  };

  const submitOnEnter = (event) => {
    if (event.key === 'Enter') submit();
  };

  return (
    <Column gap={4}>
      {signIn.isError && (
        <Alert severity="error" variant="standard" description={signInErrorMessage(signIn.error)} />
      )}
      <TextField
        id="sign-in-email"
        label="Email"
        type="email"
        name="email"
        autoComplete="username"
        placeholder="email@sample.com"
        value={email}
        onChange={(event) => setEmail(event.target.value)}
        onKeyDown={submitOnEnter}
      />
      {/* TextField has no password variant, so the reveal toggle is ours. It
          matches dental-portal's: a bare eye in the field's grey, sized by the
          24px icon slot. The classes undo IconButton's teal, its 32px circle
          and its hover tint. */}
      <TextField
        id="sign-in-password"
        label="Password"
        type={showPassword ? 'text' : 'password'}
        name="password"
        autoComplete="current-password"
        value={password}
        onChange={(event) => setPassword(event.target.value)}
        onKeyDown={submitOnEnter}
        trailingIcon={
          <IconButton
            appearance="default"
            size="small"
            className="h-6 w-6 text-text-secondary hover:bg-transparent"
            type="button"
            aria-label={showPassword ? 'Hide password' : 'Show password'}
            Icon={showPassword ? <OutlineEyeOffIcon /> : <OutlineEyeIcon />}
            onClick={() => setShowPassword((shown) => !shown)}
          />
        }
      />
      <Button onClick={submit} loading={signIn.isPending} disabled={!email || !password}>
        Login
      </Button>
    </Column>
  );
};

/**
 * The signed-out screen: email and password, Google SSO, or both, as the
 * deployment allows. Signing in swaps it for the shell; nothing here routes.
 */
const Login = () => {
  const providers = readProviders();

  return (
    <SessionScreen width="360px">
      <Column gap={6} align="center">
        <Logo appearance="gradient" mark="monogram" tone="dark" width={50} />
        {providers.credential && (
          <Box width="100%">
            <CredentialForm />
          </Box>
        )}
        {providers.google && (
          <Box width="100%">
            <GoogleLoginButton
              onClick={() => {
                window.location.href = process.env.REACT_APP_AUTH_SERVICE_GOOGLE_SSO_URL;
              }}
            />
          </Box>
        )}
      </Column>
    </SessionScreen>
  );
};

export default Login;
